import type {
    EditorAdapter,
    OpenDocumentRef,
    ProposedEdit,
    EditResolution
} from "@codenexus/react";
import { findUniqueRange } from "./codeEditSearch";

export const CODE_DOC_PREFIX = "code:";

export function codeDocId(fileId: number): string {
    return `${CODE_DOC_PREFIX}${fileId}`;
}

export function parseCodeDocId(docId: string): number | null {
    if (!docId.startsWith(CODE_DOC_PREFIX)) return null;
    const n = Number(docId.slice(CODE_DOC_PREFIX.length));
    return Number.isInteger(n) ? n : null;
}

/** One open code tab, mirrored from WorkProductEditor_Code's tab state. */
export interface CodeDocInput {
    fileId: number;
    filename: string;
    filePath: string;
    language: string;
    content: string;
}

/** An agent edit located in a file, handed to the editor-bound controller. */
export interface StagedCodeEdit {
    editId: string;
    fileId: number;
    startOffset: number;
    endOffset: number;
    old_string: string;
    new_string: string;
    rationale?: string;
}

/**
 * EditorAdapter for the Monaco code surface. Holds a registry of open tabs
 * (fed by syncOpenDocs) so the agent can list/read them, and stages edits by
 * locating old_string and delegating rendering to a controller via
 * setStagingHandler. Owns no Monaco APIs itself — it is fully unit-testable.
 */
export class MonacoEditorAdapter implements EditorAdapter {
    private docs: CodeDocInput[] = [];
    private activeFileId: number | null = null;
    private changeListeners = new Set<(refs: OpenDocumentRef[]) => void>();
    private resolveListeners = new Set<(r: EditResolution) => void>();
    private contentAccessor: ((fileId: number) => string | null) | null = null;
    private stagingHandler: ((edit: StagedCodeEdit) => void) | null = null;
    private reviewRequestHandler: ((edit: StagedCodeEdit) => void) | null = null;
    /**
     * Pending suggestions belong to the document bridge, not to a particular
     * Monaco instance. Markdown preview unmounts Monaco, so keeping these only
     * in MonacoSuggestionController made otherwise-successful edits disappear
     * when the user switched views.
     */
    private pendingEdits = new Map<string, StagedCodeEdit>();
    private readOnly = false;

    setReadOnly(readOnly: boolean): void {
        this.readOnly = readOnly;
        if (readOnly) this.stagingHandler = null;
    }

    owns(docId: string): boolean {
        return docId.startsWith(CODE_DOC_PREFIX);
    }

    setContentAccessor(fn: ((fileId: number) => string | null) | null): void {
        this.contentAccessor = fn;
    }

    setStagingHandler(fn: ((edit: StagedCodeEdit) => void) | null): void {
        this.stagingHandler = fn;
        if (fn) {
            // A new Monaco controller has mounted. Restore every unresolved
            // suggestion so switching back from Markdown preview is lossless.
            this.pendingEdits.forEach((edit) => fn(edit));
        }
    }

    /** Asks the host to reveal the editor surface where a new edit can be reviewed. */
    setReviewRequestHandler(fn: ((edit: StagedCodeEdit) => void) | null): void {
        this.reviewRequestHandler = fn;
    }

    syncOpenDocs(tabs: CodeDocInput[], activeFileId: number | null): void {
        this.docs = tabs.map((t) => ({ ...t }));
        this.activeFileId = activeFileId;
        const openFileIds = new Set(tabs.map((tab) => tab.fileId));
        for (const [editId, edit] of this.pendingEdits) {
            if (!openFileIds.has(edit.fileId)) {
                this.resolveEdit(editId, "stale", "document_closed");
            }
        }
        this.notifyChange();
    }

    private contentOf(fileId: number): string | null {
        const live = this.contentAccessor?.(fileId);
        if (live != null) return live;
        const doc = this.docs.find((d) => d.fileId === fileId);
        return doc ? doc.content : null;
    }

    private refOf(doc: CodeDocInput): OpenDocumentRef {
        const content = this.contentOf(doc.fileId) ?? doc.content;
        return {
            id: codeDocId(doc.fileId),
            title: doc.filename,
            kind: "code",
            language: doc.language,
            active: doc.fileId === this.activeFileId,
            byteLength: content.length
        };
    }

    listOpen(): OpenDocumentRef[] {
        return this.docs.map((d) => this.refOf(d));
    }

    async read(docId: string): Promise<{ content: string; ref: OpenDocumentRef }> {
        const fileId = parseCodeDocId(docId);
        const doc = fileId == null ? undefined : this.docs.find((d) => d.fileId === fileId);
        if (!doc) throw new Error("doc_not_open");
        return { content: this.contentOf(doc.fileId) ?? doc.content, ref: this.refOf(doc) };
    }

    async applyEdit(
        edit: ProposedEdit
    ): Promise<{ status: "staged" } | { status: "failed"; reason: string }> {
        if (this.readOnly) return { status: "failed", reason: "access_denied" };
        const fileId = parseCodeDocId(edit.docId);
        const doc = fileId == null ? undefined : this.docs.find((d) => d.fileId === fileId);
        if (!doc) return { status: "failed", reason: "doc_not_open" };
        const content = this.contentOf(doc.fileId) ?? doc.content;
        const found = findUniqueRange(content, edit.old_string);
        if (found.status === "not_found") return { status: "failed", reason: "old_string_not_found" };
        if (found.status === "ambiguous") return { status: "failed", reason: "old_string_ambiguous" };

        const stagedEdit: StagedCodeEdit = {
            editId: edit.editId,
            fileId: doc.fileId,
            startOffset: found.start,
            endOffset: found.end,
            old_string: edit.old_string,
            new_string: edit.new_string,
            rationale: edit.rationale
        };
        this.pendingEdits.set(edit.editId, stagedEdit);
        this.reviewRequestHandler?.(stagedEdit);
        // It is valid to stage while Markdown preview is active. Monaco will
        // receive the edit from setStagingHandler when Source Code remounts.
        this.stagingHandler?.(stagedEdit);
        return { status: "staged" };
    }

    /** Called by the controller when the user accepts/rejects a staged edit. */
    resolveEdit(editId: string, status: "accepted" | "rejected" | "stale", reason?: string): void {
        this.pendingEdits.delete(editId);
        const resolution: EditResolution =
            status === "stale"
                ? { editId, status: "stale", reason: reason ?? "stale" }
                : { editId, status };
        this.resolveListeners.forEach((l) => l(resolution));
    }

    private notifyChange(): void {
        const refs = this.listOpen();
        this.changeListeners.forEach((l) => l(refs));
    }

    onChange(l: (refs: OpenDocumentRef[]) => void): () => void {
        this.changeListeners.add(l);
        return () => {
            this.changeListeners.delete(l);
        };
    }

    onEditResolved(l: (r: EditResolution) => void): () => void {
        this.resolveListeners.add(l);
        return () => {
            this.resolveListeners.delete(l);
        };
    }
}
